import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import ReactQuill, { Quill } from 'react-quill';
import classNames from 'classnames';
import 'react-quill/dist/quill.snow.css';
import BlotFormatter from 'quill-blot-formatter';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/react-hooks';
import { GQL_UPLOAD_ASSET } from './graphql';
import { connect } from 'react-redux';
import AssetsViewerRichText from '../AssetsViewer/AssetsViewerRichText';

const useStyles = makeStyles((theme) => ({
	root: {
		margin: theme.manakin.normalMargin,
	},
	label: {
		color: theme.manakin.defaultTitleColor[500],
		fontSize: '18px',
	},
	error: {
		color: 'red',
	},
	errorMessage: {
		color: 'red',
		marginBottom: '14px',
	},
	loading: {
		height: theme.spacing(6),
		backgroundColor: theme.palette.grey[100],
		'& *': { opacity: 0, pointerEvents: 'none' },
	},
	quillContainer: {
		padding: '20px 0 0',
		'& .ql-tooltip': {
			left: '0 !important',
		},
	},
	disabledLabel: {
		color: theme.manakin.disabledFieldLabelColor,
	},
	disabledInput: {
		color: theme.manakin.disabledFieldTextColor,
	},
	disabledBorder: {
		border: 'none',
	},
}));

const imageFormatter = {
	overlay: {
		style: {
			border: '2px solid #999',
		},
	},
	align: {
		aligner: {
			applyStyle: true,
		},
		icons: {
			left: `<svg width="24" height="24" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0V0z"/><path d="M14 15H4c-.55 0-1 .45-1 1s.45 1 1 1h10c.55 0 1-.45 1-1s-.45-1-1-1zm0-8H4c-.55 0-1 .45-1 1s.45 1 1 1h10c.55 0 1-.45 1-1s-.45-1-1-1zM4 13h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zm0 8h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zM3 4c0 .55.45 1 1 1h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1z"/></svg>`,
			center: `<svg width="24" height="24" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0V0z"/><path d="M7 16c0 .55.45 1 1 1h8c.55 0 1-.45 1-1s-.45-1-1-1H8c-.55 0-1 .45-1 1zm-3 5h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zm0-8h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zm3-5c0 .55.45 1 1 1h8c.55 0 1-.45 1-1s-.45-1-1-1H8c-.55 0-1 .45-1 1zM3 4c0 .55.45 1 1 1h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1z"/></svg>`,
			right: `<svg width="24" height="24" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0V0z"/><path d="M4 21h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zm6-4h10c.55 0 1-.45 1-1s-.45-1-1-1H10c-.55 0-1 .45-1 1s.45 1 1 1zm-6-4h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1s.45 1 1 1zm6-4h10c.55 0 1-.45 1-1s-.45-1-1-1H10c-.55 0-1 .45-1 1s.45 1 1 1zM3 4c0 .55.45 1 1 1h16c.55 0 1-.45 1-1s-.45-1-1-1H4c-.55 0-1 .45-1 1z"/></svg>`,
		},
		toolbar: {
			mainStyle: { border: 'none' },
			svgStyle: {
				display: 'inline-block',
				width: '24px',
				height: '24px',
				padding: '3px',
				background: 'white',
				border: '1px solid #999999',
				verticalAlign: 'middle',
				boxSizing: 'content-box',
				cursor: 'pointer',
			},
			buttonStyle: {
				border: 'none',
				height: '32px',
				width: '32px',
			},
		},
	},
	resize: {
		handleStyle: {
			backgroundColor: '#FFFFFF',
			opacity: 1,
			width: '16px',
			height: '16px',
			borderRadius: '100%',
			border: '2px solid #999999',
		},
	},
};

const ImageAttributes = ['src', 'height', 'width', 'style', 'align', 'data-align'];
let ImageFormat = Quill.import('formats/image');

class ImageUploader {
	constructor(quill, options) {
		this.quill = quill;
		this.options = options;

		let toolbar = this.quill.getModule('toolbar');
		toolbar.addHandler('image', this.selectImage.bind(this));
	}

	selectImage() {
		// Bynder: delegate volledig naar de upload functie (opent asset viewer via promise)
		if (this.options.bynderEnabled) {
			const range = this.quill.getSelection();
			this.options.upload().then(
				(image) => {
					this.quill.insertEmbed(range.index, 'image', `${image.url}`);
				},
				(error) => {
					console.warn(error?.message || 'Upload cancelled');
				}
			);
			return;
		}

		// Standaard: native file picker
		const input = document.createElement('input');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', 'image/*');
		input.click();

		input.onchange = () => {
			const file = input.files[0];
			if (!file) return;

			const range = this.quill.getSelection();

			if (typeof this.options.upload !== 'function') {
				// Fallback: geen upload functie, gebruik base64
				const reader = new FileReader();
				reader.onload = (e) => {
					this.quill.insertEmbed(range.index, 'image', e.target.result);
				};
				reader.readAsDataURL(file);
				return;
			}

			this.options.upload(file).then(
				(image) => {
					this.quill.insertEmbed(range.index, 'image', `${image.url}`);
				},
				(error) => {
					console.warn(error?.message || 'Upload failed');
				}
			);
		};
	}
}

class Image extends ImageFormat {
	static create(value) {
		let node = super.create();
		if (value && value.url) {
			node.setAttribute('src', value.url);
			if (value.height) node.setAttribute('height', value.height);
			if (value.width) node.setAttribute('width', value.width);
			if (value.style) node.setAttribute('style', value.style);
			if (value.align) node.setAttribute('align', value.align);
			if (value['data-align']) node.setAttribute('data-align', value['data-align']);
		} else {
			node.setAttribute('src', value);
		}
		return node;
	}

	static value(node) {
		return {
			url: node.getAttribute('src'),
			height: node.getAttribute('height'),
			width: node.getAttribute('width'),
			align: node.getAttribute('align'),
			style: node.getAttribute('style'),
			'data-align': node.getAttribute('data-align'),
		};
	}

	static formats = (domNode) =>
		ImageAttributes.reduce((formats, attribute) => {
			if (domNode.hasAttribute(attribute)) formats[attribute] = domNode.getAttribute(attribute);
			return formats;
		}, {});
}

Image.blotName = 'image';
Image.tagName = 'img';

Quill.debug('error');
Quill.register('modules/blotFormatter', BlotFormatter);
Quill.register(Image);
Quill.register('modules/imageUploader', ImageUploader);

const defaultEditorModules = ['bold', 'italic', { list: 'ordered' }, { list: 'bullet' }];
const defaultEditorFormats = ['bold', 'italic', 'list', 'bullet'];

const RichTextEditor = ({
	name,
	className: classNameProp,
	label = '',
	readOnly = false,
	placeholder,
	editorModules,
	editorFormats,
	disableDefaultModulesAndFormats = false,
	loading,
	form,
	initialValue = '',
	config,
}) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const quillRef = useRef(null);
	const timeoutRef = useRef(null);
	const [uploadAsset] = useMutation(GQL_UPLOAD_ASSET);

	const bynderEnabled = config?.general?.bynderEnabled ?? false;

	const [value, setValue] = useState(initialValue);
	const [showMaxLengthWarning, setShowMaxLengthWarning] = useState(false);

	// Bynder: promise refs voor asset viewer
	const chooseAssetResolveRef = useRef(null);
	const chooseAssetRejectRef = useRef(null);
	const chooseAssetRef = useRef(null);
	const [assetViewerOpen, setAssetViewerOpen] = useState(false);

	const resetChooseAssetPromise = useCallback(() => {
		chooseAssetRef.current = new Promise((resolve, reject) => {
			chooseAssetResolveRef.current = resolve;
			chooseAssetRejectRef.current = reject;
		});
	}, []);

	useEffect(() => {
		resetChooseAssetPromise();
	}, [resetChooseAssetPromise]);

	// Sync initialValue changes
	useEffect(() => {
		setValue(initialValue);
		form.onFieldChange({ key: name, value: initialValue });
	}, [initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

	// Set initial value on mount
	useEffect(() => {
		form.onFieldChange({ key: name, value: initialValue });
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Bynder: asset viewer sluiten
	const handleCloseAssetViewer = useCallback(() => {
		if (chooseAssetRejectRef.current) chooseAssetRejectRef.current();
		setAssetViewerOpen(false);
		resetChooseAssetPromise();
	}, [resetChooseAssetPromise]);

	// Bynder pad: opent asset viewer, wacht op keuze via promise
	const handleBynderImagePick = useCallback(
		() =>
			new Promise((resolve) => {
				if (readOnly) {
					resolve({ url: '' });
					return;
				}
				setAssetViewerOpen(true);
				chooseAssetRef.current.then((image) => {
					setAssetViewerOpen(false);
					resolve(image);
				});
			}),
		[readOnly]
	);

	// Upload pad: native file picker + GraphQL mutation
	const handleUploadImagePick = useCallback(
		(file) =>
			new Promise((resolve, reject) => {
				if (readOnly) {
					resolve({ url: '' });
					return;
				}
				uploadAsset({
					variables: { file },
					context: { hasUpload: true },
				})
					.then((result) => {
						const asset = result?.data?.uploadAsset?.asset;
						if (asset?.url) {
							resolve({ url: asset.url });
						} else {
							reject(new Error('No URL in response'));
						}
					})
					.catch(reject);
			}),
		[readOnly, uploadAsset]
	);

	const handleChange = useCallback(
		(newValue) => {
			const maxLength = form.schema[name] ? form.schema[name].maxLength : null;
			const valueToCheck = newValue.replace(/(<([^>]+)>)/gi, '');
			const contentExceedsMaxLength = maxLength ? valueToCheck.length > maxLength : false;

			setValue(newValue);
			setShowMaxLengthWarning(contentExceedsMaxLength);

			clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => {
				form.onFieldChange({ key: name, value: newValue });

				const quill = quillRef.current;
				if (quill && contentExceedsMaxLength) {
					quill.setEditorSelection(quill.getEditor(), {
						index: newValue.length - 1,
						length: 0,
					});
				}
			}, 400);
		},
		[form, name]
	);

	const { errors, schema } = form;

	const finalEditorModules = disableDefaultModulesAndFormats
		? editorModules
		: [...(editorModules || []), ...defaultEditorModules];

	const finalEditorFormats = disableDefaultModulesAndFormats
		? editorFormats
		: [...(editorFormats || []), ...defaultEditorFormats];

	const className = classNames(
		classes.root,
		{ [classes.loading]: loading },
		{ [classes.error]: errors[name] && errors[name].length > 0 },
		classNameProp
	);

	return (
		<div className={className}>
			<div>
				<InputLabel
					className={classNames(classes.label, {
						[classes.disabledLabel]: readOnly,
					})}
				>
					{label}
				</InputLabel>

				<div
					className={classNames(classes.quillContainer, {
						[classes.disabledBorder]: readOnly,
						[classes.disabledInput]: readOnly,
					})}
				>
					<ReactQuill
						ref={quillRef}
						value={value}
						placeholder={placeholder}
						modules={{
							toolbar: finalEditorModules,
							imageUploader: {
								bynderEnabled,
								upload: bynderEnabled ? handleBynderImagePick : handleUploadImagePick,
							},
							clipboard: {
								matchVisual: false,
							},
							...(finalEditorFormats.includes('image') && {
								blotFormatter: imageFormatter,
							}),
						}}
						readOnly={readOnly}
						formats={finalEditorFormats}
						onChange={handleChange}
					/>

					{errors[name] &&
						errors[name].length > 0 &&
						errors[name].map((e, i) => (
							<div className={classes.errorMessage} key={i}>
								{e}
							</div>
						))}

					{showMaxLengthWarning && (
						<div className={classes.errorMessage}>
							{t('core.rich-text-editor.max-characters-exceeded', {
								amount: schema[name].maxLength,
							})}
						</div>
					)}
				</div>
			</div>

			{/* Bynder: asset viewer alleen renderen als bynder actief is */}
			{bynderEnabled && (
				<AssetsViewerRichText
					open={assetViewerOpen}
					onAssetPick={(image) => chooseAssetResolveRef.current(image)}
					onClose={handleCloseAssetViewer}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	config: state.config,
});

export default connect(mapStateToProps)(RichTextEditor);
